import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ImageLightbox } from '@odo/shared-ui';
import type { TaskBodyProps } from '../index';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import { usePatientAssets } from '../../../../hooks/usePatientAssets';
import { groupSubmissionFiles, submissionAssets } from './submissionFiles';
import { computeSubmissionProgress } from './submissionProgress';
import { useSubmissionFileAccess } from './useSubmissionFileAccess';
import { SubmissionProgressWizard } from './SubmissionProgressWizard';

/**
 * "Init - 0. Submission incomplete" viewer: the practice is still in the
 * dashboard creation flow, so staff get a read-only, step-by-step picture of
 * what has been filled in. The only forward edge (submit-case) is a practice
 * action, so the surface footer is hidden and the wizard only navigates.
 */
export function ViewSubmissionTask({ workCase: c, onPayloadChange }: TaskBodyProps) {
    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: true, bodyOwnsSubmit: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const card = usePatientCard(c.id);
    const assets = usePatientAssets(c.id);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const assetList = useMemo(() => Object.values(assets.assets), [assets.assets]);
    const files = useMemo(() => groupSubmissionFiles(assetList), [assetList]);
    const progress = useMemo(
        () => (card.data ? computeSubmissionProgress(card.data, files) : []),
        [card.data, files]
    );
    const viewableAssets = useMemo(() => submissionAssets(files), [files]);
    const access = useSubmissionFileAccess(c.id, viewableAssets);

    const { refetch: refetchCard } = card;
    const { refresh: refreshAssets } = assets;
    const handleRefresh = useCallback(() => {
        setIsRefreshing(true);
        Promise.allSettled([refetchCard(), refreshAssets()]).finally(() => setIsRefreshing(false));
    }, [refetchCard, refreshAssets]);

    if (card.isLoading || assets.isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center gap-2 text-sm text-gray-500">
                <div className="h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                Loading submission...
            </div>
        );
    }

    if (card.error || assets.error || !card.data) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 px-8 text-center">
                <ExclamationTriangleIcon className="w-8 h-8 text-gray-300" />
                <span className="text-sm text-gray-600">
                    Could not load this patient&apos;s submission.
                </span>
                <button
                    type="button"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                    Try again
                </button>
            </div>
        );
    }

    return (
        <>
            <SubmissionProgressWizard
                card={card.data}
                files={files}
                progress={progress}
                access={access}
            />
            {access.viewer &&
                createPortal(
                    <ImageLightbox
                        src={access.viewer.src}
                        alt={access.viewer.alt}
                        onClose={access.closeViewer}
                    />,
                    document.body
                )}
        </>
    );
}
